// ============================================================
// Game Configuration — single source of truth for all tuning
// ============================================================

// ── Type definitions ──

export interface NetworkConfig {
  tickRate: number;
  maxPlayers: number;
  minPlayers: number;
  connectionTimeout: number;
  pingInterval: number;
  serverPort: number;
  messageRateLimit: number;
}

export interface InterpolationConfig {
  enabled: boolean;
  minDelay: number;
  maxDelay: number;
  jitterSamples: number;
  smoothingFactor: number;
  bufferSize: number;
}

export interface PhysicsConfig {
  maxSpeed: number;
  acceleration: number;
  brakeForce: number;
  friction: number;
  steerSpeed: number;
  maxSteer: number;
  steerReturn: number;
  driftFactor: number;
  bounce: number;
  kartRadius: number;
  gravity: number;
  rampHeight: number;
  rampLength: number;
  rampWidthMult: number;
  rampLandingBoost: number;
}

export interface ItemsConfig {
  boostMultiplier: number;
  boostDuration: number;
  stunDuration: number;
  itemRespawn: number;
  projectileSpeed: number;
  projectileRadius: number;
  itemBoxRadius: number;
}

export interface RaceConfig {
  countdown: number;
  totalLaps: number;
  soloBotCount: number;
}

export interface RenderingConfig {
  shadowMapSize: number;
  shadowMapSizeMobile: number;
  maxPixelRatio: number;
  mobilePixelRatio: number;
  adaptiveFpsThreshold: number;
}

export interface ParticleGroupConfig {
  maxCount: number;
  lifetime: number;
}

export interface ParticlesConfig {
  dust: ParticleGroupConfig;
  boost: ParticleGroupConfig;
  sparks: ParticleGroupConfig;
  pickup: ParticleGroupConfig;
}

export interface TrackConfig {
  roadWidth: number;
  wallHeight: number;
  pointsPerSegment: number;
}

// ── New config sections for Mario Kart mechanics ──

export interface DriftBoostConfig {
  orangeThreshold: number;    // seconds of drift for orange boost
  redThreshold: number;       // seconds for red boost
  purpleThreshold: number;    // seconds for purple boost
  orangeDuration: number;     // boost duration for orange tier
  redDuration: number;        // boost duration for red tier
  purpleDuration: number;     // boost duration for purple tier
  boostMult: number;          // speed multiplier during drift boost
}

export interface SlipstreamConfig {
  distance: number;           // max distance behind another kart
  coneAngle: number;          // half-angle of the drafting cone (radians)
  chargeTime: number;         // seconds to fully charge slipstream
  boostDuration: number;      // seconds of boost after charge complete
  boostMult: number;          // speed multiplier during slipstream boost
}

export interface OffroadConfig {
  speedMult: number;          // max speed multiplier when off-road
  frictionMult: number;       // friction multiplier when off-road
  lateralThreshold: number;   // fraction of half-width to trigger off-road (0-1)
}

export interface CoinsConfig {
  maxCoins: number;           // max coins a kart can carry
  speedBonus: number;         // max speed bonus per coin (fraction)
  lostOnHit: number;          // coins lost when hit
  pickupRadius: number;       // collection distance
  respawnTime: number;        // seconds to respawn after collection
  coinsPerTrack: number;      // number of coin positions along track
  coinsAcross: number;        // number of coins across the road width
}

export interface ShellsConfig {
  greenSpeed: number;         // green shell travel speed
  greenBounces: number;       // max wall bounces before despawn
  redSpeed: number;           // red shell travel speed
  redTurnRate: number;        // red shell homing turn rate (radians/s)
  blueShellSpeed: number;     // blue shell travel speed
  blueShellBlastRadius: number; // explosion radius
  blueShellFlyHeight: number; // height blue shell flies at
}

export interface SpecialItemsConfig {
  lightningDuration: number;  // shrink duration in seconds
  lightningSpeedMult: number; // speed reduction while shrunk
  starDuration: number;       // invincibility duration in seconds
  starSpeedMult: number;      // speed boost during star
  blooperDuration: number;    // ink screen duration in seconds
  rouletteTime: number;       // item roulette animation time in seconds
  startBoostWindow: number;   // window before GO for start boost (seconds)
  startBoostEarly: number;    // stall duration for pressing too early
  startBoostDuration: number; // boost duration for perfect start
  trickBoostDuration: number; // boost duration for successful trick
}

export interface GameConfig {
  network: NetworkConfig;
  interpolation: InterpolationConfig;
  physics: PhysicsConfig;
  items: ItemsConfig;
  race: RaceConfig;
  rendering: RenderingConfig;
  particles: ParticlesConfig;
  track: TrackConfig;
  driftBoost: DriftBoostConfig;
  slipstream: SlipstreamConfig;
  offroad: OffroadConfig;
  coins: CoinsConfig;
  shells: ShellsConfig;
  specialItems: SpecialItemsConfig;
}

// ── Default values ──

export const config: GameConfig = {
  network: {
    tickRate: 30,
    maxPlayers: 12,
    minPlayers: 1,
    connectionTimeout: 5000,
    pingInterval: 1000,
    serverPort: 3010,
    messageRateLimit: 200,
  },

  interpolation: {
    enabled: false,
    minDelay: 50,
    maxDelay: 200,
    jitterSamples: 30,
    smoothingFactor: 0.93,
    bufferSize: 20,
  },

  physics: {
    maxSpeed: 25,
    acceleration: 15,
    brakeForce: 20,
    friction: 5,
    steerSpeed: 3.0,
    maxSteer: 0.6,
    steerReturn: 5.0,
    driftFactor: 0.85,
    bounce: 0.5,
    kartRadius: 1.2,
    gravity: 20,
    rampHeight: 2.5,
    rampLength: 8,
    rampWidthMult: 0.6,
    rampLandingBoost: 0.5,
  },

  items: {
    boostMultiplier: 1.8,
    boostDuration: 2.0,
    stunDuration: 1.5,
    itemRespawn: 10.0,
    projectileSpeed: 40,
    projectileRadius: 0.5,
    itemBoxRadius: 2.5,
  },

  race: {
    countdown: 3,
    totalLaps: 3,
    soloBotCount: 11,
  },

  rendering: {
    shadowMapSize: 1024,
    shadowMapSizeMobile: 512,
    maxPixelRatio: 1.5,
    mobilePixelRatio: 1.0,
    adaptiveFpsThreshold: 30,
  },

  particles: {
    dust: { maxCount: 150, lifetime: 0.5 },
    boost: { maxCount: 120, lifetime: 0.35 },
    sparks: { maxCount: 100, lifetime: 0.4 },
    pickup: { maxCount: 100, lifetime: 0.6 },
  },

  track: {
    roadWidth: 14,
    wallHeight: 2,
    pointsPerSegment: 20,
  },

  driftBoost: {
    orangeThreshold: 0.5,
    redThreshold: 1.5,
    purpleThreshold: 3.0,
    orangeDuration: 0.5,
    redDuration: 1.0,
    purpleDuration: 1.8,
    boostMult: 1.5,
  },

  slipstream: {
    distance: 8,
    coneAngle: 0.35,       // ~20 degrees
    chargeTime: 2.0,
    boostDuration: 1.5,
    boostMult: 1.4,
  },

  offroad: {
    speedMult: 0.6,
    frictionMult: 2.0,
    lateralThreshold: 0.85,
  },

  coins: {
    maxCoins: 10,
    speedBonus: 0.02,       // 2% per coin
    lostOnHit: 3,
    pickupRadius: 2.0,
    respawnTime: 10.0,
    coinsPerTrack: 20,
    coinsAcross: 3,
  },

  shells: {
    greenSpeed: 35,
    greenBounces: 3,
    redSpeed: 28,
    redTurnRate: 3.0,
    blueShellSpeed: 30,
    blueShellBlastRadius: 8,
    blueShellFlyHeight: 20,
  },

  specialItems: {
    lightningDuration: 5.0,
    lightningSpeedMult: 0.6,
    starDuration: 8.0,
    starSpeedMult: 1.3,
    blooperDuration: 5.0,
    rouletteTime: 2.0,
    startBoostWindow: 0.8,
    startBoostEarly: 0.5,
    startBoostDuration: 2.0,
    trickBoostDuration: 0.8,
  },
};

/**
 * Deep-merge overrides into the config object.
 * Call this before any game logic runs.
 */
export function applyConfigOverrides(overrides: Record<string, any>): void {
  deepMerge(config, overrides);
}

function deepMerge(target: any, source: any): void {
  for (const key of Object.keys(source)) {
    if (
      source[key] !== null &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key])
    ) {
      if (!target[key] || typeof target[key] !== 'object') target[key] = {};
      deepMerge(target[key], source[key]);
    } else if (source[key] !== undefined) {
      target[key] = source[key];
    }
  }
}
